import { useQueryClient } from '@tanstack/react-query';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router';
import { usePopover } from 'src/components/custom-popover';
import AppPermissions from 'src/constants/app-permissions';
import { useJoinSessionMutation, useSubscribeSessionMutation } from 'src/graphql';
import { useBoolean } from 'src/hooks/use-boolean';
import { useLocales } from 'src/locales';
import { error } from 'src/theme/palette';
import { SessionDtoType } from 'src/types/sessions';
import { sessionButtonLabel } from 'src/utils/sessions/session-button-label';

const useSessionCard = ({ session }: { session: SessionDtoType }) => {
  // #region States and hooks
  const router = useNavigate();
  const deleteDialog = useBoolean();
  const popover = usePopover();
  const queryClient = useQueryClient();
  const { t } = useLocales();
  const { enqueueSnackbar } = useSnackbar();
  const chipColor: 'success' | 'error' | 'warning' =
    session.status?.toLowerCase() === 'live'
      ? 'success'
      : session.status?.toLowerCase() === 'expired'
      ? 'error'
      : 'warning';

  const adminId = JSON.parse(localStorage.getItem('user')!)!?.id!;
  const isSubscribedToSession = !!session.user_sessions.filter(
    (session) => session.admin?.id === adminId
  ).length;

  const buttonLabel = t(sessionButtonLabel(session.status! as 'Pending') as any);

  const hasSessionStarted = moment(session.startDate).isBefore(moment());

  const isSessionExpired = moment(session.expiryDate).isAfter(moment());
  // #endregion States and hooks

  // #region Services
  const { mutate: join, isLoading: isJoining } = useJoinSessionMutation({
    onSuccess: (data) => router(session.id, { state: data.joinSession }),
  });

  const { mutate: subscribe, isLoading: isSubscribing } = useSubscribeSessionMutation({
    onSuccess: () => {
      enqueueSnackbar({
        variant: 'success',
        message: 'Subscribed successfully',
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
      });
      queryClient.invalidateQueries({ queryKey: ['Sessions'] });
    },
  });
  // #endregion Services

  // #region Context Menu Items
  const CONTEXT_ITEMS = [
    {
      onClick: deleteDialog.onTrue,
      icon: 'material-symbols:delete-outline',
      color: error.light,
      label: t('tableLabels.delete'),
      permissions: [AppPermissions.SESSIONS_DELETE],
    },
  ];

  return {
    deleteDialog,
    buttonLabel,
    isSessionExpired,
    isSubscribedToSession,
    isSubscribing,
    join,
    hasSessionStarted,
    isJoining,
    subscribe,
    t,
    chipColor,
    CONTEXT_ITEMS,
    popover,
  };
};

export default useSessionCard;
